// srcs/frontend/src/components/proposal/pages/DraftPage.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { useProposal } from '../state/ProposalContext.tsx';

import {
  useAiPromptsMutation,      // POST /ai/prompts
  useLazyGetDataQuery,       // GET /data
  useImportFileMutation,     // POST /files
  useLazyGetFileQuery,       // GET /files
} from '../../../services/solutionAPI.ts';

type ChatMsg = { role: 'User' | 'Assistant'; content: string; ts: number };

type DraftExport = {
  data_type: 'proposal'; // DB側は proposal として扱う
  session_id: string;
  seed_id: string;

  company_info: any;
  needs_info: any;
  seed_info: any;

  gap_report_md: string | null;
  roi_report_md: string | null;

  notes: string;
  chat: ChatMsg[];
  draft_md: string | null;
  feedback_draft?: string;
  updated_at: number;
};

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_MS = 5 * 60 * 1000;
const AUTOSAVE_INTERVAL_MS = 30 * 1000;
const EXPORT_DEBOUNCE_MS = 500;

function isFinishedStatus(status: any) {
  const s = String(status ?? '').trim().toLowerCase();
  return s === 'finished' || s === '完了';
}
function isFailedStatus(status: any) {
  const s = String(status ?? '').trim().toLowerCase();
  return s === 'failed' || s === '失敗';
}

// 保存ファイル名（proposal_*）
function fileName(sessionId: string, seedId: string) {
  return `proposal_${sessionId}_${seedId}.json`;
}

function byteLength(str: string) {
  try {
    return new Blob([str]).size;
  } catch {
    return str.length;
  }
}

// /data から共通的に Markdown を取り出す
function extractMarkdownGeneric(data: any): string {
  if (typeof data?.items === 'string') return data.items;
  if (typeof data?.report_md === 'string') return data.report_md;
  if (typeof data?.draft_md === 'string') return data.draft_md;
  if (typeof data?.items?.report_md === 'string') return data.items.report_md;
  if (typeof data?.items?.draft_md === 'string') return data.items.draft_md;
  if (typeof data?.items?.md === 'string') return data.items.md;

  for (const k of Object.keys(data ?? {})) {
    if (k.startsWith('items') && typeof (data as any)[k] === 'string') {
      return String((data as any)[k]);
    }
  }
  return JSON.stringify(data?.items ?? data ?? {}, null, 2);
}

export default function DraftPage() {
  const {
    sessionId,
    selectedSeedId,
    companyInfo,
    needsInfo,
    selectedSeedInfo,
    hasGapForCurrent,
    hasRoiForCurrent,
    // 不足時ハイドレーション用 Setter
    setCompanyInfo,
    setNeedsInfo,
    setSelectedSeedInfo,
  } = useProposal();

  const [postAiPrompt] = useAiPromptsMutation();
  const [triggerGetData] = useLazyGetDataQuery();
  const [postFiles] = useImportFileMutation();
  const [triggerGetFile] = useLazyGetFileQuery();

  // 左ペイン：メモ／会話／FB
  const [notes, setNotes] = useState('');
  const [chat, setChat] = useState<ChatMsg[]>([]);
  const [feedback, setFeedback] = useState('');

  // 右ペイン：ドラフト Markdown
  const [draftMd, setDraftMd] = useState<string | null>(null);

  // 参照エリア：GAP/ROI の Markdown（/dataからの自動ハイドレーション or /files復元）
  const [gapReportMd, setGapReportMd] = useState<string | null>(null);
  const [roiReportMd, setRoiReportMd] = useState<string | null>(null);

  // 状態
  const [running, setRunning] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [lastFetchAt, setLastFetchAt] = useState<number | null>(null);

  const cancelRef = useRef({ cancelled: false });

  // stale closure 回避用
  const chatRef = useRef<ChatMsg[]>([]);
  const notesRef = useRef<string>('');
  const draftRef = useRef<string | null>(null);
  const gapRef = useRef<string | null>(null);
  const roiRef = useRef<string | null>(null);
  const feedbackRef = useRef<string>('');

  useEffect(() => { chatRef.current = chat; }, [chat]);
  useEffect(() => { notesRef.current = notes; }, [notes]);
  useEffect(() => { draftRef.current = draftMd; }, [draftMd]);
  useEffect(() => { gapRef.current = gapReportMd; }, [gapReportMd]);
  useEffect(() => { roiRef.current = roiReportMd; }, [roiReportMd]);
  useEffect(() => { feedbackRef.current = feedback; }, [feedback]);

  const companyText = useMemo(() => JSON.stringify(companyInfo ?? {}, null, 2), [companyInfo]);
  const needsText = useMemo(() => JSON.stringify(needsInfo ?? {}, null, 2), [needsInfo]);
  const seedText = useMemo(() => JSON.stringify(selectedSeedInfo ?? {}, null, 2), [selectedSeedInfo]);

  // ===== Seeds の /data から不足情報（会社/ニーズ/シーズ）を自動ハイドレーション =====
  useEffect(() => {
    const needHydrateBase =
      Boolean(sessionId) &&
      Boolean(selectedSeedId) &&
      (!companyInfo || !needsInfo || !selectedSeedInfo);
    if (!needHydrateBase) return;

    (async () => {
      try {
        // seeds の一覧を取得（seed_id は 'NULL' 慣習）
        const res: any = await triggerGetData({
          session_id: sessionId!,
          data_type: 'seeds',
          seed_id: 'NULL',
        }).unwrap();

        if (!companyInfo && res?.company_info) setCompanyInfo?.(res.company_info);
        if (!needsInfo && res?.needs_info) setNeedsInfo?.(res.needs_info);

        // items から選択中 seed を補完
        if (!selectedSeedInfo && Array.isArray(res?.items)) {
          const found = res.items.find((it: any) => {
            const s = it?.seed_info ?? it?.seed ?? it;
            return s && String(s.seed_id ?? s.id ?? '') === String(selectedSeedId);
          });
          if (found) {
            const s = found.seed_info ?? found.seed ?? found;
            const d = s?.detailed_info ?? {};
            setSelectedSeedInfo?.({
              seed_id: String(s?.seed_id ?? s?.id ?? ''),
              seed_title: String(s?.title ?? s?.seed_title ?? ''),
              provider: String(s?.provider ?? ''),
              tech_domain: String(d?.technical_field ?? s?.technical_field ?? ''),
              solvable_issues: Array.isArray(d?.solvable_issues) ? d.solvable_issues : [],
              case_studies: Array.isArray(d?.case_studies) ? d.case_studies : [],
              function_overview: String(d?.function_overview ?? ''),
              differentiation_points: String(d?.differentiation_points ?? ''),
              implementation_requirements: String(d?.implementation_requirements ?? ''),
              cost_structure: String(d?.cost_structure ?? ''),
              maturity_level: String(d?.maturity_level ?? ''),
            });
          }
        }
      } catch {
        // 任意補完なので失敗しても無視
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, selectedSeedId, companyInfo, needsInfo, selectedSeedInfo]);

  // ===== GAP/ROI を /data から自動ハイドレーション =====
  useEffect(() => {
    if (!sessionId || !selectedSeedId) return;

    // GAP
    (async () => {
      if (gapRef.current) return; // 既に保持していればスキップ
      try {
        const res: any = await triggerGetData({
          session_id: sessionId,
          data_type: 'gap',
          seed_id: selectedSeedId,
        }).unwrap();
        if (isFinishedStatus(res?.status)) {
          const md = extractMarkdownGeneric(res);
          setGapReportMd(md);
        }
      } catch {
        /* 任意補完なので無視 */
      }
    })();

    // ROI
    (async () => {
      if (roiRef.current) return;
      try {
        const res: any = await triggerGetData({
          session_id: sessionId,
          data_type: 'roi',
          seed_id: selectedSeedId,
        }).unwrap();
        if (isFinishedStatus(res?.status)) {
          const md = extractMarkdownGeneric(res);
          setRoiReportMd(md);
        }
      } catch {
        /* 任意補完なので無視 */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, selectedSeedId]);

  // ===== ready 条件（緩和版）=====
  // 会社/ニーズ/シーズは空でも実行可（まずは seeds からのハイドレーションを試みる）
  // GAP/ROI は Context の has* が false でも、参照MD取得済みなら OK 扱い。
  const ready =
    Boolean(sessionId) &&
    Boolean(selectedSeedId) &&
    (hasGapForCurrent || Boolean(gapReportMd)) &&
    (hasRoiForCurrent || Boolean(roiReportMd));

  // 診断ログ（何が false かを即確認）
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[Draft ready check]', {
      sessionId,
      selectedSeedId,
      companyInfo: !!companyInfo,
      needsInfo: !!needsInfo,
      selectedSeedInfo: !!selectedSeedInfo,
      hasGapForCurrent,
      hasRoiForCurrent,
      gapReportMd: !!gapReportMd,
      roiReportMd: !!roiReportMd,
      ready,
    });
  }, [
    sessionId,
    selectedSeedId,
    companyInfo,
    needsInfo,
    selectedSeedInfo,
    hasGapForCurrent,
    hasRoiForCurrent,
    gapReportMd,
    roiReportMd,
    ready,
  ]);

  // ===== エクスポート（/files） =====
  const buildPayload = (override?: Partial<DraftExport>): DraftExport | null => {
    if (!sessionId || !selectedSeedId) return null;
    return {
      data_type: 'proposal',
      session_id: sessionId,
      seed_id: selectedSeedId,
      company_info: companyInfo ?? {},
      needs_info: needsInfo ?? {},
      seed_info: selectedSeedInfo ?? {},
      gap_report_md: gapRef.current,
      roi_report_md: roiRef.current,
      notes: notesRef.current,
      chat: chatRef.current,
      draft_md: draftRef.current,
      feedback_draft: feedbackRef.current,
      updated_at: Date.now(),
      ...(override ?? {}),
    };
  };

  // ★ ai/prompts 用 structured payload builder（Draft / proposal）
  function buildAiPromptPayload(prompt: string, feedbackOverride?: string) {
    return {
      prompt,
      session_id: sessionId!,
      data_type: 'proposal' as const,
      seed_id: selectedSeedId!,

      company_info: JSON.stringify(companyInfo ?? {}),
      needs_info: JSON.stringify(needsInfo ?? {}),
      seeds_info: JSON.stringify(selectedSeedInfo ?? {}),

      analysis_conditions: notesRef.current ?? '',
      gap_info: gapRef.current ?? '',
      roi_info: roiRef.current ?? '',

      ...(feedbackOverride
        ? { feedback: feedbackOverride }
        : {}),
    };
  }

  const doExport = async (reason: string, payloadOverride?: DraftExport) => {
    if (!sessionId || !selectedSeedId) return;
    const payload = payloadOverride ?? buildPayload();
    if (!payload) return;

    const fname = fileName(sessionId, selectedSeedId);
    const json = JSON.stringify(payload);

    console.groupCollapsed(`[DraftPage] POST /files export (${reason})`);
    console.log('endpoint:', 'POST /files');
    console.log('request.file_name:', fname);
    console.log('request.file_context.bytes:', byteLength(json));
    console.log('request.file_context(json):', payload);
    console.groupEnd();

    try {
      const res = await postFiles({ file_name: fname, file_context: json }).unwrap();
      console.groupCollapsed(`[DraftPage] POST /files export response (${reason})`);
      console.log('response:', res);
      console.groupEnd();
    } catch (e) {
      console.groupCollapsed(`[DraftPage] POST /files export ERROR (${reason})`);
      console.log('error:', e);
      console.groupEnd();
    }
  };

  // ===== /files インポート（左ペイン・参照MD・ドラフト・履歴を復元）=====
  async function importFromFiles(reason: string) {
    if (!sessionId || !selectedSeedId) return;
    const fname = fileName(sessionId, selectedSeedId);

    console.groupCollapsed('[DraftPage] GET /files import', reason);
    console.log('file_name:', fname);
    console.groupEnd();

    try {
      const res: any = await triggerGetFile({ file_name: fname }).unwrap();
      const raw = res?.file_context ?? res?.items ?? res?.content ?? res;
      const payload: DraftExport = typeof raw === 'string' ? JSON.parse(raw) : raw;

      if (!payload || payload.data_type !== 'proposal') return;

      // Context（会社・ニーズ・シーズ）
      setCompanyInfo(payload.company_info ?? {});
      setNeedsInfo(payload.needs_info ?? {});
      setSelectedSeedInfo(payload.seed_info ?? {});

      // 参照MD（GAP/ROI）
      setGapReportMd(typeof payload.gap_report_md === 'string' ? payload.gap_report_md : null);
      setRoiReportMd(typeof payload.roi_report_md === 'string' ? payload.roi_report_md : null);

      // 左ペイン（notes/feedback/chat）と右ペイン（draft）
      setNotes(payload.notes ?? '');
      setFeedback(payload.feedback_draft ?? '');
      setChat(Array.isArray(payload.chat) ? payload.chat : []);
      setDraftMd(typeof payload.draft_md === 'string' ? payload.draft_md : null);

      console.groupCollapsed('[DraftPage] import finished (hydrate left pane & refs & draft)');
      console.log('chat restored:', Array.isArray(payload.chat) ? payload.chat.length : 0);
      console.groupEnd();
    } catch (e) {
      console.warn('[DraftPage] import failed (no saved file yet?)', e);
    }
  }

  // ===== chat/draft更新のたびに保存（デバウンス） =====
  const exportTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (!ready || !sessionId || !selectedSeedId) return;

    if (exportTimerRef.current) window.clearTimeout(exportTimerRef.current);
    exportTimerRef.current = window.setTimeout(() => {
      doExport('state_change(chat/draft)');
      exportTimerRef.current = null;
    }, EXPORT_DEBOUNCE_MS);

    return () => {
      if (exportTimerRef.current) window.clearTimeout(exportTimerRef.current);
    };
  }, [
    ready,
    sessionId,
    selectedSeedId,
    chat,
    draftMd,
  ]);


  // ===== autosave（保険） =====
  useEffect(() => {
    if (!ready) return;
    const id = window.setInterval(() => doExport('autosave'), AUTOSAVE_INTERVAL_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, sessionId, selectedSeedId]);

  // ===== プロンプト =====
  function buildConversationBlock(maxTurns = 10) {
    const tail = chatRef.current.slice(-maxTurns);
    if (tail.length === 0) return '（会話履歴なし）';
    return tail.map((m) => `${m.role}: ${m.content}`).join('\n');
  }

  function buildPromptInitial(): string {
    return `
あなたは経験豊富なソリューション営業担当者です。以下の入力（ニーズ／シーズ／GAP／ROI／条件）を統合し、**顧客が意思決定できる**具体的・実用的な提案書ドラフト（Markdown）を作成してください。

【企業情報（参考）】
${JSON.stringify(companyInfo ?? {}, null, 2)}

【ニーズ情報】
${JSON.stringify(needsInfo ?? {}, null, 2)}

【シーズ情報（選択中）】
${JSON.stringify(selectedSeedInfo ?? {}, null, 2)}

【GAP分析結果（参考）】
${gapRef.current ?? '（未取得：不足する場合は妥当な前提を明示して扱う）'}

【ROI試算結果（参考）】
${roiRef.current ?? '（未取得：不足する場合は妥当な前提を明示して扱う）'}

【条件・特記事項（任意）】
${notesRef.current || '（特になし）'}

【出力要件（厳守）】
- 日本語で、**見出しを明確**にし、**箇条書き**や**表**を適切に用いる。
- **重要な数値や効果は太字（例：**ROI 85%**）**で強調する。
- 入力が不足する箇所は、**保守的な仮置き**をおき、**根拠**（類似事例・一般相場など）を併記する。
- 「条件・特記事項」は**全体へ反映**する（トーン／優先順位／制約への配慮など）。

【章立て（固定）】
1. サマリー
2. 課題認識
3. 提案ソリューション
4. 投資対効果
5. 計画
6. 今後の進め方

※「投資対効果」では、GAP/ROIの要点を簡潔に再掲し、数値は**太字**で示してください。
`.trim();
  }

  function buildPromptImprove(userFeedback: string): string {
    return `
あなたは提案書ドラフトの改善担当です。**前回ドラフト**と**直近会話**、**条件・特記事項**、**ユーザーのフィードバック**を踏まえ、章立てを維持したままドラフトを改善してください。

【前回ドラフト（Markdown）】
${draftRef.current ?? '（まだドラフトなし）'}

【会話履歴（直近）】
${buildConversationBlock(10)}

【条件・特記事項】
${notesRef.current || '（特になし）'}

【ユーザーフィードバック】
${userFeedback}

【改善要件】
- 表現の明瞭化、読みやすさ向上（見出し・箇条書き・表の最適化）
- **重要数値・効果の太字強調**の抜け漏れ是正
- GAP/ROIの要点の**整合性**（一貫性・根拠の明記）
- 「条件・特記事項」のドラフト全体への**反映**（トーン／優先順位／制約）
- 章立て（1〜6）を維持すること

【出力形式】
- 改善後の**完全な提案書ドラフト（Markdown）**（章立ては1〜6を維持）
- 末尾に **変更点** と **改善理由** を箇条書きで明記
`.trim();
  }

  // ===== GET /data ポーリング（proposal） =====
  async function pollUntilFinished(baseChatSnapshot: ChatMsg[], reason: string) {
    if (!sessionId || !selectedSeedId) return;

    setRunning(true);
    setTimedOut(false);
    cancelRef.current.cancelled = false;

    const start = Date.now();
    let attempt = 0;

    try {
      while (Date.now() - start < POLL_MAX_MS) {
        if (cancelRef.current.cancelled) break;

        attempt += 1;
        const query = {
          session_id: sessionId,
          data_type: 'proposal' as const,
          seed_id: selectedSeedId,
        };

        console.groupCollapsed(`[DraftPage] GET /data poll (#${attempt}) reason=${reason}`);
        console.log('endpoint:', 'GET /data');
        console.log('query:', query);
        console.groupEnd();

        let data: any;
        try {
          data = await triggerGetData(query).unwrap();

          console.groupCollapsed(`[DraftPage] GET /data poll response (#${attempt})`);
          console.log('status:', data?.status);
          console.log('updated_date:', data?.updated_date);
          console.log('response(raw):', data);
          console.groupEnd();
        } catch (e) {
          console.groupCollapsed(`[DraftPage] GET /data poll ERROR (#${attempt})`);
          console.log('error:', e);
          console.groupEnd();

          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          continue;
        }

        setLastFetchAt(Date.now());

        if (isFinishedStatus(data?.status)) {
          const md = extractMarkdownGeneric(data);

          console.groupCollapsed(`[DraftPage] GET /data finished reason=${reason}`);
          console.log('final markdown length:', md?.length ?? 0);
          console.log('final markdown(md):', md);
          console.groupEnd();

          const assistantMsg: ChatMsg = { role: 'Assistant', content: md, ts: Date.now() };
          const chatNext = [...baseChatSnapshot, assistantMsg];

          setDraftMd(md);
          setChat(chatNext);

          // 自動保存
          await doExport(`response(${reason})`, {
            ...(buildPayload() as DraftExport),
            chat: chatNext,
            draft_md: md,
            updated_at: Date.now(),
          });
          return;
        }

        if (isFailedStatus(data?.status)) {
          const msg = `提案書生成 failed: ${String(data?.status)}`;

          console.groupCollapsed(`[DraftPage] GET /data failed reason=${reason}`);
          console.log('message:', msg);
          console.log('response:', data);
          console.groupEnd();

          const assistantMsg: ChatMsg = { role: 'Assistant', content: msg, ts: Date.now() };
          const chatNext = [...baseChatSnapshot, assistantMsg];

          setDraftMd(msg);
          setChat(chatNext);

          await doExport(`response_failed(${reason})`, {
            ...(buildPayload() as DraftExport),
            chat: chatNext,
            draft_md: msg,
            updated_at: Date.now(),
          });
          return;
        }

        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      }

      console.groupCollapsed(`[DraftPage] GET /data timed out reason=${reason}`);
      console.log('timeout_ms:', POLL_MAX_MS);
      console.groupEnd();

      setTimedOut(true);
    } finally {
      setRunning(false);
    }
  }

  // ===== 生成 =====
  const onGenerate = async () => {
    if (!ready || !sessionId || !selectedSeedId) return;

    const prompt = buildPromptInitial();
    const userMsg: ChatMsg = { role: 'User', content: '提案書ドラフトを生成', ts: Date.now() };

    const baseChat = chatRef.current;
    const chatAfterUser = [...baseChat, userMsg];

    setChat(chatAfterUser);
    setDraftMd(null);
    setTimedOut(false);

    // 発話時点で保存
    await doExport('user_message_generate', {
      ...(buildPayload() as DraftExport),
      chat: chatAfterUser,
      draft_md: null,
      updated_at: Date.now(),
    });

    const body = buildAiPromptPayload(prompt);

    console.groupCollapsed('[DraftPage] POST /ai/prompts (generate)');
    console.log('endpoint:', 'POST /ai/prompts');
    console.log('body.session_id:', body.session_id);
    console.log('body.data_type:', body.data_type);
    console.log('body.seed_id:', body.seed_id);
    console.log('body.prompt.length:', body.prompt.length);
    console.log('body.prompt:', body.prompt);
    console.groupEnd();

    try {
      await postAiPrompt(body).unwrap();
    } catch (e) {
      console.groupCollapsed('[DraftPage] POST /ai/prompts ERROR (generate)');
      console.log('error:', e);
      console.groupEnd();

      const msg = `POST /ai/prompts に失敗しました: ${String((e as any)?.message ?? e)}`;
      const assistantMsg: ChatMsg = { role: 'Assistant', content: msg, ts: Date.now() };
      const chatNext = [...chatAfterUser, assistantMsg];

      setChat(chatNext);
      setDraftMd(msg);

      await doExport('ai_prompts_failed', {
        ...(buildPayload() as DraftExport),
        chat: chatNext,
        draft_md: msg,
        updated_at: Date.now(),
      });
      return;
    }

    await pollUntilFinished(chatAfterUser, 'generate');
  };

  // ===== 改善 =====
  const onImprove = async () => {
    if (!ready || !sessionId || !selectedSeedId) return;
    const fb = feedback.trim();
    if (!fb) return;

    const prompt = buildPromptImprove(fb);
    const userMsg: ChatMsg = { role: 'User', content: fb, ts: Date.now() };
    setFeedback('');
    setTimedOut(false);

    const baseChat = chatRef.current;
    const chatAfterUser = [...baseChat, userMsg];
    setChat(chatAfterUser);

    await doExport('user_message_improve', {
      ...(buildPayload() as DraftExport),
      chat: chatAfterUser,
      updated_at: Date.now(),
    });

    const body = buildAiPromptPayload(prompt, fb);

    console.groupCollapsed('[DraftPage] POST /ai/prompts (improve)');
    console.log('endpoint:', 'POST /ai/prompts');
    console.log('body.session_id:', body.session_id);
    console.log('body.data_type:', body.data_type);
    console.log('body.seed_id:', body.seed_id);
    console.log('body.prompt.length:', body.prompt.length);
    console.log('body.prompt:', body.prompt);
    console.groupEnd();

    try {
      await postAiPrompt(body).unwrap();
    } catch (e) {
      console.groupCollapsed('[DraftPage] POST /ai/prompts ERROR (improve)');
      console.log('error:', e);
      console.groupEnd();

      const msg = `POST /ai/prompts（改善）に失敗しました: ${String((e as any)?.message ?? e)}`;
      const assistantMsg: ChatMsg = { role: 'Assistant', content: msg, ts: Date.now() };
      const chatNext = [...chatAfterUser, assistantMsg];

      setChat(chatNext);
      setDraftMd(msg);

      await doExport('ai_prompts_failed_improve', {
        ...(buildPayload() as DraftExport),
        chat: chatNext,
        draft_md: msg,
        updated_at: Date.now(),
      });
      return;
    }

    await pollUntilFinished(chatAfterUser, 'improve');
  };

  // ===== 再取得（/files → /data） =====
  const onRefetch = async () => {
    if (!ready || !sessionId || !selectedSeedId) return;
    setTimedOut(false);

    console.groupCollapsed('[DraftPage] refetch start');
    console.log('action:', 'GET /files import -> GET /data');
    console.groupEnd();

    // 1) まず /files から復元（左ペイン・参照MD・ドラフト・履歴）
    await importFromFiles('refetch');

    // 2) その後 /data をポーリングして最新化（完了時に自動保存も走る）
    await pollUntilFinished(chatRef.current, 'refetch');
  };

  // ===== 手動保存 =====
  const onSave = async () => {
    if (!sessionId || !selectedSeedId) return;
    await doExport('manual_save');
  };

  useEffect(() => () => { cancelRef.current.cancelled = true; }, []);

  // 未充足項目の可視化（親切さ向上）
  const missing: string[] = [];
  if (!sessionId) missing.push('sessionId');
  if (!selectedSeedId) missing.push('seed_id');
  if (!(hasGapForCurrent || gapReportMd)) missing.push('GAP完了');
  if (!(hasRoiForCurrent || roiReportMd)) missing.push('ROI完了');

  return (
    <Box sx={{ height: 'calc(100vh - 32px)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, p: 2 }}>
      {/* 左：操作・参照 */}
      <Box sx={{ overflow: 'auto', pr: 1 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>提案書ドラフト</Typography>

        {!ready && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Draft を生成するには「シーズ選択」＋「GAP完了」＋「ROI完了」が必要です。<br />
            不足: {missing.length ? missing.join(' / ') : '（判定中…）'}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button variant="contained" disabled={!ready || running} onClick={onGenerate}>
              ドラフト生成
            </Button>

            {running && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">取得中…（最大5分）</Typography>
              </Stack>
            )}

            {lastFetchAt && (
              <Typography variant="caption" color="text.secondary">
                最終取得: {new Date(lastFetchAt).toLocaleTimeString()}
              </Typography>
            )}
          </Stack>

          {timedOut && (
            <Alert severity="info" sx={{ mt: 1 }}>
              5分経っても finished になりませんでした。再取得してください。
            </Alert>
          )}
        </Paper>

        {/* 参照（company／needs／seed／GAP／ROI） */}
        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>企業情報（参照のみ）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField value={companyText} multiline minRows={5} fullWidth size="small" InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>ニーズ情報（参照のみ）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField value={needsText} multiline minRows={8} fullWidth size="small" InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>選択シーズ情報（参照のみ）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField value={seedText} multiline minRows={8} fullWidth size="small" InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>GAP情報（参照のみ）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField value={gapReportMd ?? ''} multiline minRows={8} fullWidth size="small" InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>ROI情報（参照のみ）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField value={roiReportMd ?? ''} multiline minRows={8} fullWidth size="small" InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>特記事項（任意）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              placeholder="例）提案書のトーン / 章立て / 強調したいポイント など"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              minRows={4}
              fullWidth
              size="small"
              disabled={!ready || running}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>改善（フィードバック）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="改善したい点（表現・構成・数値根拠など）"
              multiline
              minRows={3}
              fullWidth
              size="small"
              sx={{ mb: 1 }}
              disabled={!ready || running}
            />
            <Button variant="contained" disabled={!ready || running || !feedback.trim()} onClick={onImprove}>
              改善して再生成
            </Button>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 700 }}>生成履歴（対話）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {chat.length === 0 ? (
              <Typography variant="body2" color="text.secondary">まだ履歴はありません。</Typography>
            ) : (
              <Stack spacing={1}>
                {chat.map((m, idx) => (
                  <Box key={idx} sx={{ p: 1, borderRadius: 1, bgcolor: m.role === 'User' ? '#e3f2fd' : '#f5f5f5' }}>
                    <Typography variant="caption" color="text.secondary">
                      {m.role} ・ {new Date(m.ts).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {m.content}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* 右：結果 */}
      <Box sx={{ overflow: 'auto', pl: 1 }}>
        {/* 右上 sticky：再取得 + 保存 */}
        <Box sx={{ position: 'sticky', top: 0, zIndex: 2, display: 'flex', justifyContent: 'flex-end', gap: 1, pb: 1, bgcolor: 'background.default' }}>
          <Button variant="outlined" size="small" disabled={!ready || running} onClick={onRefetch}>再取得</Button>
          <Button variant="outlined" size="small" disabled={!ready || running} onClick={onSave}>保存</Button>
        </Box>

        {!draftMd ? (
          <Box />
        ) : (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>提案書ドラフト</Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ bgcolor: '#fafafa', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {draftMd}
              </ReactMarkdown>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
}